import { useEffect, useState } from "react";
import {
  createPricingController,
  type PricingState,
  type QuoteGateway,
} from "../../../../packages/pvc-product/pricing-controller";
import { createPvcQuoteGateway } from "../../../../packages/pvc-product/pvc-shop-api-adapters";
import { pvcQuoteBinding } from "../../../../packages/pvc-product/pvc-shop-api-bindings";
import { createShopApiGraphqlClient } from "../../../../packages/pvc-product/shop-api-transport";
import { PVC_STOREFRONT_SCHEMA } from "../pvc-runtime";

type SharedQuoteRequest = {
  promise: ReturnType<QuoteGateway["fetch"]>;
  abortController: AbortController;
  consumers: number;
};

function abortedRequestError(): Error {
  const error = new Error("Quote request aborted");
  error.name = "AbortError";
  return error;
}

/**
 * Coalesces only requests whose complete quote input is identical and only
 * while the request is in flight. Successful prices are never cached here:
 * a later request still goes back to the server, which remains the sole
 * pricing authority.
 */
export function createCoalescingQuoteGateway(gateway: QuoteGateway): QuoteGateway {
  const inFlight = new Map<string, SharedQuoteRequest>();

  return {
    fetch(input, signal) {
      if (signal?.aborted) return Promise.reject(abortedRequestError());

      const requestKey = JSON.stringify(input);
      let shared = inFlight.get(requestKey);

      if (!shared) {
        const abortController = new AbortController();
        const promise = gateway.fetch(input, abortController.signal);
        shared = { promise, abortController, consumers: 0 };
        inFlight.set(requestKey, shared);

        const cleanup = () => {
          if (inFlight.get(requestKey) === shared) inFlight.delete(requestKey);
        };
        void promise.then(cleanup, cleanup);
      }

      shared.consumers += 1;
      let released = false;
      const release = () => {
        if (released) return;
        released = true;
        signal?.removeEventListener("abort", release);
        shared!.consumers -= 1;

        if (shared!.consumers === 0 && inFlight.get(requestKey) === shared) {
          inFlight.delete(requestKey);
          shared!.abortController.abort();
        }
      };

      signal?.addEventListener("abort", release, { once: true });
      return shared.promise.finally(release);
    },
  };
}

let sharedHomepageQuoteGateway: QuoteGateway | null = null;

function getHomepageQuoteGateway(): QuoteGateway {
  if (sharedHomepageQuoteGateway) return sharedHomepageQuoteGateway;

  sharedHomepageQuoteGateway = createCoalescingQuoteGateway(
    createPvcQuoteGateway({
      client: createShopApiGraphqlClient({
        endpoint: import.meta.env.VITE_SHOP_API_URL || "/shop-api",
      }),
      binding: pvcQuoteBinding,
    }),
  );
  return sharedHomepageQuoteGateway;
}

export function useHomepagePvcQuote(
  width: number | null,
  height: number | null,
  quantity: number,
  enabled = true,
) {
  const key = JSON.stringify([width, height, quantity, enabled]);
  const [attempt, setAttempt] = useState(0);
  const [snapshot, setSnapshot] = useState<{
    key: string;
    state: PricingState;
  } | null>(null);
  useEffect(() => {
    if (!enabled || width === null || height === null) return;
    const pricing = createPricingController({
      gateway: getHomepageQuoteGateway(),
    });
    setSnapshot({ key, state: { status: "initial-loading", quote: null } });
    const unsubscribe = pricing.subscribe((state) =>
      setSnapshot({ key, state }),
    );
    const timer = setTimeout(() => {
      void pricing.quote(
        {
          widthCm: width,
          heightCm: height,
          quantity,
          materialId: "pvc-3mm",
          addonIds: [],
          requestedDate: null,
        },
        PVC_STOREFRONT_SCHEMA.schemaVersion,
      );
    }, 400);
    return () => {
      clearTimeout(timer);
      unsubscribe();
      pricing.dispose();
    };
  }, [width, height, quantity, enabled, key, attempt]);
  // Mask the previous configuration immediately, including the debounce window.
  const state: PricingState = !enabled
    ? { status: "idle", quote: null }
    : snapshot?.key === key
      ? snapshot.state
      : { status: "initial-loading", quote: null };
  return { state, retry: () => setAttempt((value) => value + 1) };
}
